import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, of, shareReplay, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RESTHeartLanguageResponse } from '../models/restheart-language-response';
import { RESTHeartTranslationResponse } from '../models/restheart-translation-response';
import { GlobalStateProviderService } from './global-state-provider.service';
import { FormControllerService } from './form-controller.service';
import { ToastrService } from 'ngx-toastr';
import { LOCAL_STORAGE_KEYS } from '../constants/storage-keys';
import { getLanguageWithFallback } from '../utils/storage-utils';

@Injectable({
  providedIn: 'root'
})
export class EregistrationsTranslationService {
  private readonly _language!: string;

  serviceIdsToLoad: Set<string> = new Set<string>();
  execChange: Subject<string> = new Subject<string>();

  // Base (global-only) translations, set once at app init
  private baseTranslations: Record<string, string> = {};

  // Short-lived cache: Map<cacheKey, { serviceData, merged, timestamp }>
  private translationCache = new Map<
    string,
    {
      serviceData: Record<string, string>;
      merged: Record<string, string>;
      timestamp: number;
    }
  >();
  private readonly CACHE_TTL_MS = 60_000; // 60 seconds

  get language(): string {
    return this._language;
  }

  set language(value: string) {
    if (value !== this._language) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, value);
      let lastTask = null;
      if (this.globalStateProviderService.processHistory?.length > 0) {
        lastTask =
          this.globalStateProviderService.processHistory[
            this.globalStateProviderService.processHistory?.length - 1
          ];
      }
      if (
        this.globalStateProviderService.formioForm &&
        lastTask &&
        lastTask.assignee === 'applicant' &&
        lastTask.status === 'filepending'
      ) {
        this.formControllerService
          .saveFile(
            this.globalStateProviderService.formOpts.file_id,
            this.globalStateProviderService.formSubmission.data
          )
          .subscribe(() => {
            window.location.reload();
          });
      } else {
        window.location.reload();
      }
    }
  }

  updateLanguage(language: string) {
    this.language = language;
  }

  public languages$ = this.http
    .get<RESTHeartLanguageResponse>(
      `${environment.RESTHEART_URL}documents/settings/language/`
    )
    .pipe(
      map((response) => {
        // Sort languages alphabetically by code for consistent ordering
        // NOTE: This can be changed to sort by usage, priority, or left unsorted
        // based on UX/business requirements. Current implementation ensures
        // consistent order between old DS and new DS.
        return (
          response.values?.sort((a, b) => a.code.localeCompare(b.code)) || []
        );
      }),
      shareReplay(1)
    );

  constructor(
    private readonly http: HttpClient,
    private readonly globalStateProviderService: GlobalStateProviderService,
    private readonly formControllerService: FormControllerService,
    private readonly toastr: ToastrService
  ) {
    this._language = getLanguageWithFallback();
  }

  /**
   * Called once at app init to store the base (global) translations.
   * This is the foundation that service translations are merged on top of.
   */
  setBaseTranslations(translations: Record<string, string>): void {
    this.baseTranslations = { ...translations };
  }

  /**
   * Fetches service translations (with short-lived cache), merges with base,
   * updates window.GLOBAL_TRANSLATIONS, and returns the merged dict.
   *
   * Single method for all consumers — form component, my-account, etc.
   */
  mergeServiceTranslations(
    translationsId: string,
    lang: string
  ): Observable<Record<string, string>> {
    const cacheKey = `${translationsId}:${lang}`;
    const cached = this.translationCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL_MS) {
      window.GLOBAL_TRANSLATIONS = cached.merged;
      return of(cached.merged);
    }

    return this.loadTranslationsForSingleService(translationsId, lang).pipe(
      map((serviceDict) => {
        const merged = { ...this.baseTranslations, ...serviceDict };
        this.translationCache.set(cacheKey, {
          serviceData: serviceDict,
          merged,
          timestamp: Date.now()
        });
        window.GLOBAL_TRANSLATIONS = merged;
        return merged;
      })
    );
  }

  /**
   * Resets GLOBAL_TRANSLATIONS to base-only (no service overrides).
   * Use when no service is selected.
   */
  resetToBaseTranslations(): Record<string, string> {
    window.GLOBAL_TRANSLATIONS = { ...this.baseTranslations };
    return window.GLOBAL_TRANSLATIONS;
  }

  idPushed(serviceId: string) {
    this.serviceIdsToLoad.add(serviceId);
    this.execChange.next(serviceId);
  }

  /**
   * Load translations for a specific service and language from RESTHeart API.
   * Always makes an HTTP request (caching is handled by mergeServiceTranslations).
   * @param serviceId The service/translations document ID to load
   * @param language The language code (e.g. 'en', 'fr', 'es')
   * @returns Observable of translation dictionary for the specified language
   */
  loadTranslationsForSingleService(
    serviceId: string,
    language: string
  ): Observable<Record<string, string>> {
    // Fetch from API with cache-busting timestamp
    const cacheBuster = Date.now();
    return this.http
      .get<RESTHeartTranslationResponse>(
        `${environment.RESTHEART_URL}documents/translation/${serviceId}/?_t=${cacheBuster}`,
        { headers: { 'Cache-Control': 'no-cache, no-store' } }
      )
      .pipe(
        map((response) => {
          // eslint-disable-next-line security/detect-object-injection -- language from environment config
          return response.translations?.[language] || {};
        }),
        catchError((error) => {
          if (environment.DEBUG_MODE) {
            console.warn(
              `Failed to load translations for service ${serviceId}, language ${language}:`,
              error
            );
            this.toastr.warning(
              $localize`:@@Could not load service translations:Could not load service translations. Using global translations instead.`,
              $localize`:@@Translation Loading:Translation Loading`
            );
          }
          return of({});
        })
      );
  }
}
